import { create } from 'zustand';

/** Stable empty filter object — avoids spurious list-refetch effects that compare by reference. */
export const EMPTY_DOCUMENT_LIST_QUERY_EXTRA = Object.freeze({});

function isSameDocumentListQueryExtra(a, b) {
    const ae = a && typeof a === 'object' ? a : EMPTY_DOCUMENT_LIST_QUERY_EXTRA;
    const be = b && typeof b === 'object' ? b : EMPTY_DOCUMENT_LIST_QUERY_EXTRA;
    if (ae === be) return true;
    const keysA = Object.keys(ae);
    const keysB = Object.keys(be);
    if (keysA.length !== keysB.length) return false;
    return keysA.every((k) => ae[k] === be[k]);
}

const defaultPagination = {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPreviousPage: false,
};

const useDocumentStore = create((set, get) => ({
    documents: [],
    columnFilters: {},
    uniqueValues: {},
    documentListScope: 'full',
    paginationData: { ...defaultPagination },
    loading: false,
    error: null,
    lastUpdated: null,

    selectedDocument: null,
    selectedDocumentLoading: false,

    documentListQueryExtra: EMPTY_DOCUMENT_LIST_QUERY_EXTRA,
    setDocumentListQueryExtra: (extra) => {
        const normalized =
            extra && typeof extra === 'object' && Object.keys(extra).length > 0
                ? extra
                : EMPTY_DOCUMENT_LIST_QUERY_EXTRA;
        if (isSameDocumentListQueryExtra(get().documentListQueryExtra, normalized)) return;
        set({ documentListQueryExtra: normalized });
    },

    setLoading: (loading) => set({ loading }),
    setError: (error) => set({ error }),
    clearError: () => set({ error: null }),

    setReportListState: ({
        documents,
        paginationData,
        uniqueValues,
        columnFilters,
        listScope,
    }) =>
        set({
            documents: documents ?? [],
            documentListScope: listScope || 'full',
            paginationData: paginationData ?? { ...defaultPagination },
            uniqueValues: uniqueValues ?? {},
            ...(columnFilters !== undefined ? { columnFilters } : {}),
            lastUpdated: new Date().toISOString(),
        }),

    setColumnFilters: (columnFilters) => set({ columnFilters }),

    setSelectedDocument: (selectedDocument) => set({ selectedDocument }),
    setSelectedDocumentLoading: (selectedDocumentLoading) => set({ selectedDocumentLoading }),

    isApprovalDialogOpen: false,
    documentForApproval: null,
    isAddDialogOpen: false,
    createDialogDraft: null,
    isUpdateDialogOpen: false,
    documentToUpdate: null,

    openAddDialog: (draft = null) => set({ isAddDialogOpen: true, createDialogDraft: draft || null }),
    closeAddDialog: () => set({ isAddDialogOpen: false, createDialogDraft: null }),

    openUpdateDialog: (doc) => set({ isUpdateDialogOpen: true, documentToUpdate: doc }),
    closeUpdateDialog: () => set({ isUpdateDialogOpen: false, documentToUpdate: null }),

    openApprovalDialog: (doc) => set({ isApprovalDialogOpen: true, documentForApproval: doc }),
    closeApprovalDialog: () => set({ isApprovalDialogOpen: false, documentForApproval: null }),

    reset: () =>
        set({
            documents: [],
            documentListScope: 'full',
            paginationData: { ...defaultPagination },
            loading: false,
            error: null,
            lastUpdated: null,
            isApprovalDialogOpen: false,
            documentForApproval: null,
        }),
}));

export { useDocumentStore };
